import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef } from 'react';
import type { TableOfContentsHeadingProps } from './types';

type Props = {
  activeId: string;
  distanceToSectionWrapper: number;
  headings: TableOfContentsHeadingProps[];
  headingLevel: number;
  setActiveId: Dispatch<SetStateAction<string>>;
  tocId: string;
};

type Entry = { isIntersecting: boolean; intersectionRatio: number; target: Element };

export function useHeadingsObserver({
  activeId,
  distanceToSectionWrapper,
  headings,
  headingLevel,
  setActiveId,
  tocId,
}: Props) {
  const { current: intersectionRatios } = useRef<Record<string, number>>({});
  const scrollTrackingIsEnabled = useRef(true);

  const reenableScrollTracking = useCallback(() => {
    scrollTrackingIsEnabled.current = true;
  }, []);

  const pauseScrollTracking = useCallback(() => {
    scrollTrackingIsEnabled.current = false;
  }, []);

  useEffect(() => {
    const tableOfContentElement = document.getElementById(tocId);
    let observer: undefined | IntersectionObserver;
    if (tableOfContentElement) {
      const handleObserver = (entries: Entry[]) => {
        entries.forEach(entry => {
          const sectionId = entry.target.getElementsByTagName(`h${headingLevel}`)?.[0]?.id;
          if (!sectionId) return;
          const intersectionRatio = entry.intersectionRatio;
          intersectionRatios[sectionId] = intersectionRatio;
        });

        if (scrollTrackingIsEnabled.current) {
          let maxIntersectionRatio = activeId ? intersectionRatios[activeId] : 0;
          let mostVisibleSectionId = activeId;
          Object.entries(intersectionRatios).forEach(([sectionId, intersectionRatio]) => {
            if (intersectionRatio > maxIntersectionRatio) {
              maxIntersectionRatio = intersectionRatio;
              mostVisibleSectionId = sectionId;
            }
          });
          if (maxIntersectionRatio > 0) {
            setActiveId(mostVisibleSectionId);
          }
        }
      };

      observer = new IntersectionObserver(handleObserver, {
        root: null,
        threshold: Array.from({ length: 11 }, (_, i) => i * 0.1),
      });

      const elements = headings.map(({ id }) => document.getElementById(id));

      elements.forEach(el => {
        let sectionElement;
        if (el && observer) {
          let iterator: HTMLElement | null | undefined = el;
          for (let index = 0; index < distanceToSectionWrapper; index++) {
            iterator = iterator?.parentElement;
          }
          sectionElement = iterator;
          if (sectionElement) {
            observer.observe(sectionElement);
          }
        }
      });

      reenableScrollTracking();

      return () => {
        observer?.disconnect();
      };
    }
  }, [headings]);

  return {
    pauseScrollTracking,
    reenableScrollTracking,
  };
}
